"use client";

import React from "react";
import { motion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, Star, ShieldCheck } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
const plumbers = [{
  id: 1,
  name: "James Wilson",
  role: "Plumbing Specialist",
  experience: "Pipes & Water Lines",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_james.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_james.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_james.webp-wvc-sizes",
  serviceArea: "Atlanta Metro"
}, {
  id: 2,
  name: "Sarah Jenkins",
  role: "Plumbing Specialist",
  experience: "Drains & Fixtures",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_sarah.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_sarah.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_sarah.png-wvc-sizes",
  serviceArea: "Marietta & Cobb"
}, {
  id: 3,
  name: "Michael Chang",
  role: "Plumbing Specialist",
  experience: "Sewer & Main Lines",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_michael.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_michael.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_michael.png-wvc-sizes",
  serviceArea: "Gwinnett & North GA"
}, {
  id: 4,
  name: "David Rodriguez",
  role: "Plumbing Specialist",
  experience: "Leak Detection & Valves",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_david.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_david.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_david.webp-wvc-sizes",
  serviceArea: "Alpharetta & Roswell"
}, {
  id: 5,
  name: "Emily Carter",
  role: "Plumbing Specialist",
  experience: "Residential Systems",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_emily.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_emily.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_emily.png-wvc-sizes",
  serviceArea: "Decatur & DeKalb"
}, {
  id: 6,
  name: "Robert Taylor",
  role: "Plumbing Specialist",
  experience: "Water Heaters & Pipes",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_robert.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_robert.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumber_robert.webp-wvc-sizes",
  serviceArea: "Metro Atlanta"
}];
const specialties = [{
  plumberId: 1,
  name: "Pipe Repair"
}, {
  plumberId: 1,
  name: "Water Heaters"
}, {
  plumberId: 2,
  name: "Drain Cleaning"
}, {
  plumberId: 2,
  name: "Installations"
}, {
  plumberId: 3,
  name: "Commercial"
}, {
  plumberId: 3,
  name: "Sewer Lines"
}, {
  plumberId: 4,
  name: "Urgent Repairs"
}, {
  plumberId: 4,
  name: "Leak Detection"
}, {
  plumberId: 5,
  name: "Residential"
}, {
  plumberId: 5,
  name: "Fixtures"
}, {
  plumberId: 6,
  name: "Gas Lines"
}, {
  plumberId: 6,
  name: "Remodeling"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function MasterPlumbersGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="masterplumbersgrid" className="bg-background py-16 md:py-24 lg:py-32" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="mx-auto max-w-3xl">
            <Badge variant="secondary" className="mb-4">
              <Award className="mr-1 size-3" />
              Verified Professionals
            </Badge>
            <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Meet Local Plumbing Specialists
            </h2>
            <p className="text-muted-foreground font-sans text-lg md:text-xl">
              Connect with independent local plumbers through our marketplace network for reliable home plumbing services.
            </p>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate={isVisible ? "show" : "hidden"} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plumbers.map((plumber, index) => <motion.div key={plumber.id} variants={itemVariants} data-index={index}>
              <Card className="group flex h-full flex-col overflow-hidden transition-all duration-300 hover:shadow-lg">
                <div className="relative overflow-hidden">
                  <AspectRatio ratio={4 / 3}>
                    <img src={plumber.imageSrc} data-wvc-srcset={plumber.imageSrcset} data-wvc-sizes={plumber.imageSizes} alt={`Portrait of ${plumber.name}, ${plumber.role}`} className="size-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </AspectRatio>
                </div>

                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-default text-foreground flex items-center gap-2 text-xl font-bold">
                        {plumber.name}
                        <CheckCircle2 className="text-primary size-5 shrink-0" />
                      </h3>
                      <p className="text-muted-foreground font-sans text-sm font-medium">
                        {plumber.role}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-grow pb-6">
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="text-muted-foreground size-4 shrink-0" />
                      <span className="text-muted-foreground font-sans text-sm">
                        Focus: <strong className="text-foreground font-semibold">{plumber.experience}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="text-muted-foreground size-4 shrink-0" />
                      <span className="text-muted-foreground font-sans text-sm">
                        Area: <strong className="text-foreground font-semibold">{plumber.serviceArea}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-foreground font-sans text-xs font-semibold uppercase tracking-wider">
                      Specialties
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {specialties.filter(s => s.plumberId === plumber.id).map((specialty, sIndex) => <Badge key={sIndex} variant="outline" className="bg-accent/50 font-sans text-xs font-normal" data-index={sIndex}>
                            {specialty.name}
                          </Badge>)}
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="pt-0">
                  <Button asChild className="w-full font-semibold" size="lg">
                    <Link to="#bookingrequest">
                      Request a Quote
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}